import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard } from '../../context/WizardContext';
import WizardProgress from '../../components/WizardProgress';
import { listDataSources, DataSource } from '../../api/client';
import DataSourceConfig from '../../components/DataSourceConfig';
import '../MappingWizard.css';

const Step3DataSources: React.FC = () => {
  const navigate = useNavigate();
  const { state, saveDraft } = useWizard();

  const [availableDataSources, setAvailableDataSources] = useState<DataSource[]>([]);
  const [selectedSources, setSelectedSources] = useState(state.dataSources);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    try {
      const data = await listDataSources();
      setAvailableDataSources(data);
    } catch (err) {
      // Error silently handled
    } finally {
      setLoading(false);
    }
  };

  const addDataSource = (dsId: string, type: 'master' | 'detail') => {
    // Check if primary already exists
    const hasPrimary = selectedSources.some((s) => s.role === 'primary');

    const role = type === 'master' && !hasPrimary ? 'primary' : 'secondary';

    setSelectedSources([
      ...selectedSources,
      {
        id: dsId,
        type,
        role,
      },
    ]);
  };

  const removeDataSource = (dsId: string) => {
    setSelectedSources(selectedSources.filter((s) => s.id !== dsId));
  };

  const setPrimary = (dsId: string) => {
    setSelectedSources(
      selectedSources.map((s) => ({
        ...s,
        role: s.id === dsId ? 'primary' : 'secondary',
      }))
    );
  };

  const getDataSourceInfo = (dsId: string) => {
    return availableDataSources.find((ds) => ds.id === dsId);
  };

  const handleNext = async () => {
    if (selectedSources.length === 0) {
      alert('Bitte füge mindestens eine Datenvorlage hinzu');
      return;
    }

    const hasPrimary = selectedSources.some((s) => s.role === 'primary');
    if (!hasPrimary) {
      alert('Bitte wähle eine primäre Datenvorlage aus');
      return;
    }

    setSaving(true);
    try {
      await saveDraft(3, { dataSources: selectedSources });
      navigate('/wizard/relationships');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveDraft(3, { dataSources: selectedSources });
      alert('Entwurf gespeichert!');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleBack = () => {
    navigate('/wizard/template');
  };

  const handleDataSourceCreated = (newDataSource: DataSource) => {
    setAvailableDataSources([...availableDataSources, newDataSource]);
    setShowCreateForm(false);
    alert(`Datenvorlage "${newDataSource.name}" erfolgreich erstellt!`);
  };

  return (
    <>
      <WizardProgress currentStep={3} totalSteps={5} />
      <div className="wizard-step">
        <h2>Datenvorlagen hinzufügen</h2>
        <p className="step-description">
          Füge eine oder mehrere Datenvorlagen hinzu. Die <strong>primäre</strong> Datenvorlage
          bestimmt, wie viele PDFs erzeugt werden (z.B. ein PDF pro Person).
        </p>

        {/* Selected Data Sources */}
        <div className="selected-sources">
          <h3>Ausgewählte Datenvorlagen ({selectedSources.length})</h3>
          {selectedSources.length === 0 ? (
            <p className="no-data">Keine Datenvorlagen ausgewählt</p>
          ) : (
            <div className="source-list">
              {selectedSources.map((source) => {
                const dsInfo = getDataSourceInfo(source.id);
                return (
                  <div key={source.id} className="source-item selected">
                    <div className="source-header">
                      <div>
                        <strong>{dsInfo?.name || source.id}</strong>
                        <span className={`badge badge-${source.type}`}>
                          {source.type === 'master' ? 'Stammdaten' : 'Detaildaten'}
                        </span>
                        {source.role === 'primary' && (
                          <span className="badge badge-primary">Primär</span>
                        )}
                      </div>
                      <button
                        onClick={() => removeDataSource(source.id)}
                        className="btn-remove"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="source-meta">
                      <span>Typ: {dsInfo?.type}</span>
                    </div>
                    {source.role !== 'primary' && source.type === 'master' && (
                      <button
                        onClick={() => setPrimary(source.id)}
                        className="btn-link"
                      >
                        Als primäre Vorlage setzen
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Available Data Sources */}
        <div className="available-sources">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Verfügbare Datenvorlagen</h3>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="btn-primary"
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              {showCreateForm ? '✕ Abbrechen' : '+ Neue Datenvorlage'}
            </button>
          </div>

          {showCreateForm && (
            <div className="create-form-container" style={{
              background: '#f9f9f9',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '2px solid #667eea'
            }}>
              <DataSourceConfig onSuccess={handleDataSourceCreated} />
            </div>
          )}

          {!showCreateForm && (
            <>
              <p className="help-text">
                <strong>Stammdaten:</strong> Hauptdaten (z.B. Personen) - ein PDF pro Eintrag<br />
                <strong>Detaildaten:</strong> Positionsdaten (z.B. Anwesenheitszeiten) - werden verknüpft
              </p>

              {loading ? (
                <p>Lade Datenvorlagen...</p>
              ) : availableDataSources.length === 0 ? (
                <p className="no-data">
                  Keine Datenvorlagen vorhanden. Klicke auf "+ Neue Datenvorlage" um eine zu erstellen.
                </p>
              ) : (
                <div className="source-grid">
                  {availableDataSources
                    .filter((ds) => !selectedSources.find((s) => s.id === ds.id))
                    .map((ds) => (
                      <div key={ds.id} className="source-card">
                        <div className="source-info">
                          <h4>{ds.name}</h4>
                          <p>
                            <span className="badge">{ds.type}</span>
                          </p>
                          <small>{new Date(ds.created_at).toLocaleDateString()}</small>
                        </div>
                        <div className="source-actions">
                          <button
                            onClick={() => addDataSource(ds.id, 'master')}
                            className="btn-small btn-master"
                          >
                            + Als Stammdaten
                          </button>
                          <button
                            onClick={() => addDataSource(ds.id, 'detail')}
                            className="btn-small btn-detail"
                          >
                            + Als Detaildaten
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="wizard-actions">
          <button onClick={handleBack} className="btn-secondary">
            ← Zurück
          </button>
          <button
            onClick={handleSaveDraft}
            className="btn-secondary"
            disabled={savingDraft || saving}
            style={{ marginLeft: 'auto' }}
          >
            {savingDraft ? 'Speichere...' : 'Entwurf speichern'}
          </button>
          <button
            onClick={handleNext}
            className="btn-primary"
            disabled={selectedSources.length === 0 || saving || savingDraft}
          >
            {saving ? 'Speichere...' : 'Weiter →'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Step3DataSources;
